// ============================================
// MEDFLOW - Finances Page
// Dashboard financiero con analytics y pagos
// ============================================

import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DollarSign,
  TrendingUp,
  Receipt,
  CreditCard,
  Banknote,
  ArrowUpDown,
  Building2,
  Users,
  Package,
  Calendar,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Plus,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { boxesApi, financeApi, patientsApi, professionalsApi, servicesApi } from '@/lib/api';
import { isSupabaseAuthEnabled } from '@/lib/auth-config';
import {
  createSupabasePayment,
  deleteSupabasePayment,
  loadSupabaseBoxes,
  loadSupabaseFinance,
  loadSupabasePatientsList,
  loadSupabaseProfessionals,
  loadSupabaseServices,
  updateSupabasePayment,
} from '@/lib/supabase-data';
import { cn, formatCurrency, formatDateTime } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

// ============ TYPES ============

interface MethodBreakdown {
  method: string;
  total: number;
  count: number;
}

interface StatusBreakdown {
  status: string;
  total: number;
  count: number;
}

interface SummaryData {
  totalRevenue: number;
  totalPayments: number;
  averageTicket: number;
  byMethod: MethodBreakdown[];
  byStatus: StatusBreakdown[];
}

interface ServiceRevenue {
  serviceId: string;
  serviceName: string;
  total: number;
  count: number;
  averageTicket: number;
}

interface ProfessionalRevenue {
  professionalId: string;
  professionalName: string;
  specialty: string;
  total: number;
  count: number;
  averageTicket: number;
}

interface BoxRevenue {
  boxId: string;
  boxName: string;
  total: number;
  count: number;
}

interface DailyData {
  date: string;
  total: number;
  count: number;
}

interface PaymentRecord {
  id: string;
  amount: number;
  paymentMethod: string;
  status: string;
  description?: string;
  paidAt: string;
  patient?: { name: string };
  professional?: { user: { name: string } };
  service?: { name: string };
  box?: { name: string };
}

interface SelectOption {
  id: string;
  name: string;
}

interface PaymentFormState {
  patientId: string;
  professionalId: string;
  serviceId: string;
  boxId: string;
  amount: string;
  paymentMethod: string;
  status: string;
  description: string;
  paidAt: string;
}

// ============ HELPERS ============

const methodLabels: Record<string, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  other: 'Otro',
};

const methodIcons: Record<string, typeof DollarSign> = {
  cash: Banknote,
  card: CreditCard,
  transfer: ArrowUpDown,
  other: Receipt,
};

const methodColors: Record<string, string> = {
  cash: 'bg-green-500',
  card: 'bg-blue-500',
  transfer: 'bg-purple-500',
  other: 'bg-gray-500',
};

const statusLabels: Record<string, string> = {
  pending: 'Pendiente',
  completed: 'Completado',
  refunded: 'Reembolsado',
  partial: 'Parcial',
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  refunded: 'bg-red-100 text-red-800',
  partial: 'bg-blue-100 text-blue-800',
};

const defaultPaymentForm = (): PaymentFormState => ({
  patientId: '',
  professionalId: '',
  serviceId: '',
  boxId: '',
  amount: '',
  paymentMethod: 'card',
  status: 'completed',
  description: '',
  paidAt: new Date().toISOString().slice(0, 16),
});

type ActiveTab = 'overview' | 'services' | 'professionals' | 'boxes' | 'payments';

// ============ COMPONENT ============

export function FinancesPage() {
  const { clinic } = useAuthStore();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState<PaymentRecord | null>(null);
  const [paymentForm, setPaymentForm] = useState<PaymentFormState>(defaultPaymentForm());

  const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
  const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
  const dateParams = { startDate, endDate };

  // ============ QUERIES ============

  const { data: supabaseFinanceData, isLoading: supabaseFinanceLoading } = useQuery({
    queryKey: ['finance', 'supabase', clinic?.id, startDate, endDate],
    queryFn: () => loadSupabaseFinance(clinic?.id ?? '', dateParams),
    enabled: isSupabaseAuthEnabled && Boolean(clinic?.id),
  });

  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['finance', 'summary', startDate, endDate],
    queryFn: () => financeApi.summary(dateParams),
    enabled: !isSupabaseAuthEnabled,
  });

  const { data: byServiceData } = useQuery({
    queryKey: ['finance', 'by-service', startDate, endDate],
    queryFn: () => financeApi.byService(dateParams),
    enabled: !isSupabaseAuthEnabled && (activeTab === 'overview' || activeTab === 'services'),
  });

  const { data: byProfessionalData } = useQuery({
    queryKey: ['finance', 'by-professional', startDate, endDate],
    queryFn: () => financeApi.byProfessional(dateParams),
    enabled: !isSupabaseAuthEnabled && (activeTab === 'overview' || activeTab === 'professionals'),
  });

  const { data: byBoxData } = useQuery({
    queryKey: ['finance', 'by-box', startDate, endDate],
    queryFn: () => financeApi.byBox(dateParams),
    enabled: !isSupabaseAuthEnabled && (activeTab === 'overview' || activeTab === 'boxes'),
  });

  const { data: dailyData } = useQuery({
    queryKey: ['finance', 'daily', startDate, endDate],
    queryFn: () => financeApi.daily(dateParams),
    enabled: !isSupabaseAuthEnabled && activeTab === 'overview',
  });

  const { data: paymentsData, isLoading: paymentsLoading } = useQuery({
    queryKey: ['finance', 'payments', startDate, endDate],
    queryFn: () => financeApi.listPayments({ startDate, endDate, limit: 50 }),
    enabled: !isSupabaseAuthEnabled && activeTab === 'payments',
  });

  const { data: patientOptions = [] } = useQuery({
    queryKey: ['finance', 'payment-form', 'patients', clinic?.id, isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: async () => {
      if (isSupabaseAuthEnabled) {
        return loadSupabasePatientsList(clinic?.id ?? '');
      }

      const response = await patientsApi.list({ limit: 200 });
      return (((response?.data as Array<Record<string, unknown>>) ?? []).map((patient) => ({
        id: String(patient.id),
        name: String(patient.name ?? 'Paciente'),
      })) as SelectOption[]);
    },
    enabled: activeTab === 'payments' && (isSupabaseAuthEnabled ? Boolean(clinic?.id) : true),
  });

  const { data: professionalOptions = [] } = useQuery({
    queryKey: ['finance', 'payment-form', 'professionals', clinic?.id, isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: async () => {
      if (isSupabaseAuthEnabled) {
        const professionals = await loadSupabaseProfessionals(clinic?.id ?? '', true);
        return professionals.map((professional) => ({ id: professional.id, name: professional.user.name }));
      }

      const response = await professionalsApi.list(true);
      return (((response?.data as Array<Record<string, unknown>>) ?? []).map((professional) => ({
        id: String(professional.id),
        name: String((professional.user as Record<string, unknown> | undefined)?.name ?? 'Profesional'),
      })) as SelectOption[]);
    },
    enabled: activeTab === 'payments' && (isSupabaseAuthEnabled ? Boolean(clinic?.id) : true),
  });

  const { data: serviceOptions = [] } = useQuery({
    queryKey: ['finance', 'payment-form', 'services', clinic?.id, isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: async () => {
      if (isSupabaseAuthEnabled) {
        const services = await loadSupabaseServices(clinic?.id ?? '');
        return services.filter((service) => service.isActive).map((service) => ({ id: service.id, name: service.name }));
      }

      const response = await servicesApi.list(true);
      return (((response?.data as Array<Record<string, unknown>>) ?? []).map((service) => ({
        id: String(service.id),
        name: String(service.name ?? 'Servicio'),
      })) as SelectOption[]);
    },
    enabled: activeTab === 'payments' && (isSupabaseAuthEnabled ? Boolean(clinic?.id) : true),
  });

  const { data: boxOptions = [] } = useQuery({
    queryKey: ['finance', 'payment-form', 'boxes', clinic?.id, isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: async () => {
      if (isSupabaseAuthEnabled) {
        const boxes = await loadSupabaseBoxes(clinic?.id ?? '', true);
        return boxes.filter((box) => box.isActive).map((box) => ({ id: box.id, name: box.name }));
      }

      const response = await boxesApi.list(true);
      return (((response?.data as Array<Record<string, unknown>>) ?? []).map((box) => ({
        id: String(box.id),
        name: String(box.name ?? 'Box'),
      })) as SelectOption[]);
    },
    enabled: activeTab === 'payments' && (isSupabaseAuthEnabled ? Boolean(clinic?.id) : true),
  });

  const summary = isSupabaseAuthEnabled
    ? supabaseFinanceData?.summary || {
        totalRevenue: 0,
        totalPayments: 0,
        averageTicket: 0,
        byMethod: [],
        byStatus: [],
      }
    : (summaryData?.data as SummaryData) || {
        totalRevenue: 0,
        totalPayments: 0,
        averageTicket: 0,
        byMethod: [],
        byStatus: [],
      };

  const serviceRevenues = useMemo(
    () => (isSupabaseAuthEnabled ? (supabaseFinanceData?.byService ?? []) : ((byServiceData?.data as ServiceRevenue[] | undefined) ?? [])),
    [supabaseFinanceData?.byService, byServiceData?.data]
  );
  const professionalRevenues = useMemo(
    () => (isSupabaseAuthEnabled ? (supabaseFinanceData?.byProfessional ?? []) : ((byProfessionalData?.data as ProfessionalRevenue[] | undefined) ?? [])),
    [supabaseFinanceData?.byProfessional, byProfessionalData?.data]
  );
  const boxRevenues = useMemo(
    () => (isSupabaseAuthEnabled ? (supabaseFinanceData?.byBox ?? []) : ((byBoxData?.data as BoxRevenue[] | undefined) ?? [])),
    [supabaseFinanceData?.byBox, byBoxData?.data]
  );
  const dailyRevenues = useMemo(
    () => (isSupabaseAuthEnabled ? (supabaseFinanceData?.daily ?? []) : ((dailyData?.data as DailyData[] | undefined) ?? [])),
    [supabaseFinanceData?.daily, dailyData?.data]
  );
  const paymentsList = useMemo(
    () => (isSupabaseAuthEnabled ? (supabaseFinanceData?.payments ?? []) : ((paymentsData?.data as PaymentRecord[] | undefined) ?? [])),
    [supabaseFinanceData?.payments, paymentsData?.data]
  );

  const currentSummaryLoading = isSupabaseAuthEnabled ? supabaseFinanceLoading : summaryLoading;
  const currentPaymentsLoading = isSupabaseAuthEnabled ? supabaseFinanceLoading : paymentsLoading;

  const createPaymentMutation = useMutation({
    mutationFn: async () => {
      const amount = Number(paymentForm.amount);
      if (!paymentForm.patientId) {
        throw new Error('Debes seleccionar un paciente.');
      }
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error('El monto debe ser mayor a 0.');
      }

      const payload = {
        patientId: paymentForm.patientId,
        professionalId: paymentForm.professionalId || undefined,
        serviceId: paymentForm.serviceId || undefined,
        boxId: paymentForm.boxId || undefined,
        amount,
        paymentMethod: paymentForm.paymentMethod,
        status: paymentForm.status,
        description: paymentForm.description.trim() || undefined,
        paidAt: new Date(paymentForm.paidAt).toISOString(),
      };

      if (isSupabaseAuthEnabled) {
        await createSupabasePayment(clinic?.id ?? '', payload as never);
        return;
      }

      await financeApi.createPayment(payload);
    },
    onSuccess: () => {
      setShowCreateModal(false);
      setPaymentForm(defaultPaymentForm());
      queryClient.invalidateQueries({ queryKey: ['finance'] });
    },
  });

  const updatePaymentMutation = useMutation({
    mutationFn: async () => {
      if (!editingPayment) {
        throw new Error('No hay pago seleccionado.');
      }

      const amount = Number(paymentForm.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error('El monto debe ser mayor a 0.');
      }

      const payload = {
        amount,
        paymentMethod: paymentForm.paymentMethod,
        status: paymentForm.status,
        description: paymentForm.description.trim() || undefined,
      };

      if (isSupabaseAuthEnabled) {
        await updateSupabasePayment(editingPayment.id, payload as never);
        return;
      }

      await financeApi.updatePayment(editingPayment.id, payload);
    },
    onSuccess: () => {
      setEditingPayment(null);
      setPaymentForm(defaultPaymentForm());
      queryClient.invalidateQueries({ queryKey: ['finance'] });
    },
  });

  const deletePaymentMutation = useMutation({
    mutationFn: async (paymentId: string) => {
      if (isSupabaseAuthEnabled) {
        await deleteSupabasePayment(paymentId);
        return;
      }

      await financeApi.deletePayment(paymentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance'] });
    },
  });

  // Max daily revenue for bar chart scaling
  const maxDaily = useMemo(() => {
    return Math.max(...dailyRevenues.map(d => d.total), 1);
  }, [dailyRevenues]);

  // Max service revenue for bar scaling
  const maxServiceRevenue = useMemo(() => {
    return Math.max(...serviceRevenues.map(s => s.total), 1);
  }, [serviceRevenues]);

  const maxProfessionalRevenue = useMemo(() => {
    return Math.max(...professionalRevenues.map(p => p.total), 1);
  }, [professionalRevenues]);

  // ============ NAV ============

  const tabs = [
    { id: 'overview' as const, label: 'Resumen', icon: BarChart3 },
    { id: 'services' as const, label: 'Por Servicio', icon: Package },
    { id: 'professionals' as const, label: 'Por Profesional', icon: Users },
    { id: 'boxes' as const, label: 'Por Box', icon: Building2 },
    { id: 'payments' as const, label: 'Pagos', icon: Receipt },
  ];

  const openCreateModal = () => {
    setEditingPayment(null);
    setPaymentForm(defaultPaymentForm());
    setShowCreateModal(true);
  };

  const openEditModal = (payment: PaymentRecord) => {
    setShowCreateModal(false);
    setEditingPayment(payment);
    setPaymentForm({
      patientId: '',
      professionalId: '',
      serviceId: '',
      boxId: '',
      amount: String(payment.amount),
      paymentMethod: payment.paymentMethod,
      status: payment.status,
      description: payment.description ?? '',
      paidAt: payment.paidAt.slice(0, 16),
    });
  };

  const closePaymentModal = () => {
    setShowCreateModal(false);
    setEditingPayment(null);
    setPaymentForm(defaultPaymentForm());
  };

  const handleDeletePayment = (payment: PaymentRecord) => {
    if (confirm(`¿Eliminar el pago de ${payment.patient?.name || 'paciente'} por ${formatCurrency(payment.amount)}?`)) {
      deletePaymentMutation.mutate(payment.id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finanzas</h1>
          <p className="text-gray-600">
            Control financiero de la clínica
          </p>
        </div>

        {/* Month selector */}
        <div className="flex items-center gap-2 bg-white rounded-lg border px-3 py-2">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium min-w-[140px] text-center capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: es })}
          </span>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors',
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Summary Cards - always visible */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-green-500">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(summary.totalRevenue)}</p>
                <p className="text-sm text-gray-500">Ingresos Totales</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-500">
                <Receipt className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.totalPayments}</p>
                <p className="text-sm text-gray-500">Total Pagos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-purple-500">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(summary.averageTicket)}</p>
                <p className="text-sm text-gray-500">Ticket Promedio</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-yellow-500">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold capitalize">
                  {format(currentMonth, 'MMM yyyy', { locale: es })}
                </p>
                <p className="text-sm text-gray-500">Período</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          summary={summary}
          dailyRevenues={dailyRevenues}
          serviceRevenues={serviceRevenues}
          professionalRevenues={professionalRevenues}
          maxDaily={maxDaily}
          maxServiceRevenue={maxServiceRevenue}
          maxProfessionalRevenue={maxProfessionalRevenue}
          isLoading={currentSummaryLoading}
        />
      )}

      {activeTab === 'services' && (
        <ServiceTab data={serviceRevenues} maxRevenue={maxServiceRevenue} />
      )}

      {activeTab === 'professionals' && (
        <ProfessionalTab data={professionalRevenues} maxRevenue={maxProfessionalRevenue} />
      )}

      {activeTab === 'boxes' && (
        <BoxTab data={boxRevenues} />
      )}

      {activeTab === 'payments' && (
        <PaymentsTab
          data={paymentsList}
          isLoading={currentPaymentsLoading}
          onCreate={openCreateModal}
          onEdit={openEditModal}
          onDelete={handleDeletePayment}
          deletingId={deletePaymentMutation.isPending ? deletePaymentMutation.variables : undefined}
        />
      )}

      {(showCreateModal || editingPayment) && (
        <PaymentModal
          title={editingPayment ? 'Editar pago' : 'Registrar pago'}
          form={paymentForm}
          onChange={(field, value) => setPaymentForm((current) => ({ ...current, [field]: value }))}
          onClose={closePaymentModal}
          onSubmit={() => {
            if (editingPayment) {
              updatePaymentMutation.mutate();
              return;
            }

            createPaymentMutation.mutate();
          }}
          isSubmitting={createPaymentMutation.isPending || updatePaymentMutation.isPending}
          patientOptions={patientOptions}
          professionalOptions={professionalOptions}
          serviceOptions={serviceOptions}
          boxOptions={boxOptions}
          isEditing={Boolean(editingPayment)}
        />
      )}
    </div>
  );
}

// ============ OVERVIEW TAB ============

function OverviewTab({
  summary,
  dailyRevenues,
  serviceRevenues,
  professionalRevenues,
  maxDaily,
  maxServiceRevenue,
  maxProfessionalRevenue,
  isLoading,
}: {
  summary: SummaryData;
  dailyRevenues: DailyData[];
  serviceRevenues: ServiceRevenue[];
  professionalRevenues: ProfessionalRevenue[];
  maxDaily: number;
  maxServiceRevenue: number;
  maxProfessionalRevenue: number;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-6">
      {/* Payment Method Breakdown */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Por Método de Pago</CardTitle>
          </CardHeader>
          <CardContent>
            {summary.byMethod.length === 0 ? (
              <EmptyState message="Sin datos de pagos" />
            ) : (
              <div className="space-y-4">
                {summary.byMethod.map((m) => {
                  const Icon = methodIcons[m.method] || Receipt;
                  const color = methodColors[m.method] || 'bg-gray-500';
                  const pct = summary.totalRevenue > 0
                    ? Math.round((m.total / summary.totalRevenue) * 100)
                    : 0;

                  return (
                    <div key={m.method} className="flex items-center gap-3">
                      <div className={cn('w-8 h-8 rounded-md flex items-center justify-center', color)}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium">
                            {methodLabels[m.method] || m.method}
                          </span>
                          <span className="text-sm text-gray-500">{pct}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={cn('h-2 rounded-full', color)}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-semibold whitespace-nowrap">
                        {formatCurrency(m.total)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Por Estado</CardTitle>
          </CardHeader>
          <CardContent>
            {summary.byStatus.length === 0 ? (
              <EmptyState message="Sin datos de estado" />
            ) : (
              <div className="space-y-3">
                {summary.byStatus.map((s) => (
                  <div key={s.status} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-3">
                      <Badge className={statusColors[s.status] || 'bg-gray-100 text-gray-800'}>
                        {statusLabels[s.status] || s.status}
                      </Badge>
                      <span className="text-sm text-gray-600">{s.count} pagos</span>
                    </div>
                    <span className="font-semibold">{formatCurrency(s.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Daily Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ingresos Diarios</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyRevenues.length === 0 ? (
            <EmptyState message="Sin ingresos registrados en este período" />
          ) : (
            <div className="flex items-end gap-1 h-48 overflow-x-auto pb-6 relative">
              {dailyRevenues.map((d) => {
                const heightPct = Math.max((d.total / maxDaily) * 100, 2);
                const dayNum = d.date.split('-')[2];
                return (
                  <div
                    key={d.date}
                    className="flex flex-col items-center flex-1 min-w-[20px] group relative"
                  >
                    <div
                      className="w-full bg-primary/80 rounded-t hover:bg-primary transition-colors min-w-[14px] max-w-[32px] mx-auto"
                      style={{ height: `${heightPct}%` }}
                      title={`${d.date}: ${formatCurrency(d.total)} (${d.count} pagos)`}
                    />
                    <span className="text-[10px] text-gray-400 mt-1 absolute -bottom-5">
                      {dayNum}
                    </span>
                    {/* Tooltip on hover */}
                    <div className="absolute -top-14 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                      {formatCurrency(d.total)}
                      <br />
                      {d.count} pagos
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Services & Professionals side by side */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top Servicios</CardTitle>
          </CardHeader>
          <CardContent>
            {serviceRevenues.length === 0 ? (
              <EmptyState message="Sin datos de servicios" />
            ) : (
              <div className="space-y-3">
                {serviceRevenues.slice(0, 5).map((s, idx) => (
                  <div key={s.serviceId} className="flex items-center gap-3">
                    <span className="text-sm font-bold text-gray-400 w-5">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium truncate">{s.serviceName}</span>
                        <span className="text-sm font-semibold ml-2">{formatCurrency(s.total)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-500 h-1.5 rounded-full"
                          style={{ width: `${(s.total / maxServiceRevenue) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top Profesionales</CardTitle>
          </CardHeader>
          <CardContent>
            {professionalRevenues.length === 0 ? (
              <EmptyState message="Sin datos de profesionales" />
            ) : (
              <div className="space-y-3">
                {professionalRevenues.slice(0, 5).map((p, idx) => (
                  <div key={p.professionalId} className="flex items-center gap-3">
                    <span className="text-sm font-bold text-gray-400 w-5">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium truncate">{p.professionalName}</span>
                        <span className="text-sm font-semibold ml-2">{formatCurrency(p.total)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-purple-500 h-1.5 rounded-full"
                          style={{ width: `${(p.total / maxProfessionalRevenue) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============ SERVICE TAB ============

function ServiceTab({ data, maxRevenue }: { data: ServiceRevenue[]; maxRevenue: number }) {
  if (data.length === 0) return <EmptyState message="Sin datos de servicios para este período" />;

  const totalRevenue = data.reduce((sum, s) => sum + s.total, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Ingresos por Servicio</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="pb-3 font-medium">Servicio</th>
                <th className="pb-3 font-medium text-right">Pagos</th>
                <th className="pb-3 font-medium text-right">Ticket Prom.</th>
                <th className="pb-3 font-medium text-right">Total</th>
                <th className="pb-3 font-medium text-right w-24">%</th>
                <th className="pb-3 font-medium w-32"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((s) => {
                const pct = totalRevenue > 0 ? Math.round((s.total / totalRevenue) * 100) : 0;
                return (
                  <tr key={s.serviceId} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-3 font-medium">{s.serviceName}</td>
                    <td className="py-3 text-right text-gray-600">{s.count}</td>
                    <td className="py-3 text-right text-gray-600">{formatCurrency(s.averageTicket)}</td>
                    <td className="py-3 text-right font-semibold">{formatCurrency(s.total)}</td>
                    <td className="py-3 text-right text-gray-500">{pct}%</td>
                    <td className="py-3">
                      <div className="w-full bg-gray-200 rounded-full h-2 ml-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${(s.total / maxRevenue) * 100}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-bold">
                <td className="pt-3">Total</td>
                <td className="pt-3 text-right">{data.reduce((sum, s) => sum + s.count, 0)}</td>
                <td className="pt-3 text-right">
                  {formatCurrency(Math.round(totalRevenue / Math.max(data.reduce((sum, s) => sum + s.count, 0), 1)))}
                </td>
                <td className="pt-3 text-right">{formatCurrency(totalRevenue)}</td>
                <td className="pt-3 text-right">100%</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ PROFESSIONAL TAB ============

function ProfessionalTab({ data, maxRevenue }: { data: ProfessionalRevenue[]; maxRevenue: number }) {
  if (data.length === 0) return <EmptyState message="Sin datos de profesionales para este período" />;

  const totalRevenue = data.reduce((sum, p) => sum + p.total, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Ingresos por Profesional</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="pb-3 font-medium">Profesional</th>
                <th className="pb-3 font-medium">Especialidad</th>
                <th className="pb-3 font-medium text-right">Atenciones</th>
                <th className="pb-3 font-medium text-right">Ticket Prom.</th>
                <th className="pb-3 font-medium text-right">Total</th>
                <th className="pb-3 font-medium w-32"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((p) => (
                <tr key={p.professionalId} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-3 font-medium">{p.professionalName}</td>
                  <td className="py-3 text-gray-600">{p.specialty}</td>
                  <td className="py-3 text-right text-gray-600">{p.count}</td>
                  <td className="py-3 text-right text-gray-600">{formatCurrency(p.averageTicket)}</td>
                  <td className="py-3 text-right font-semibold">{formatCurrency(p.total)}</td>
                  <td className="py-3">
                    <div className="w-full bg-gray-200 rounded-full h-2 ml-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full"
                        style={{ width: `${(p.total / maxRevenue) * 100}%` }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-bold">
                <td className="pt-3">Total</td>
                <td></td>
                <td className="pt-3 text-right">{data.reduce((sum, p) => sum + p.count, 0)}</td>
                <td className="pt-3 text-right">
                  {formatCurrency(Math.round(totalRevenue / Math.max(data.reduce((sum, p) => sum + p.count, 0), 1)))}
                </td>
                <td className="pt-3 text-right">{formatCurrency(totalRevenue)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ BOX TAB ============

function BoxTab({ data }: { data: BoxRevenue[] }) {
  if (data.length === 0) return <EmptyState message="Sin datos de boxes para este período" />;

  const totalRevenue = data.reduce((sum, b) => sum + b.total, 0);
  const maxRevenue = Math.max(...data.map(b => b.total), 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Ingresos por Box / Sala</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((b) => {
            const pct = totalRevenue > 0 ? Math.round((b.total / totalRevenue) * 100) : 0;
            return (
              <div
                key={b.boxId}
                className="p-4 rounded-lg border hover:shadow transition-shadow"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-primary" />
                  <span className="font-semibold">{b.boxName}</span>
                </div>
                <p className="text-2xl font-bold mb-1">{formatCurrency(b.total)}</p>
                <p className="text-sm text-gray-500 mb-3">{b.count} atenciones · {pct}% del total</p>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full"
                    style={{ width: `${(b.total / maxRevenue) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ============ PAYMENTS TAB ============

function PaymentsTab({
  data,
  isLoading,
  onCreate,
  onEdit,
  onDelete,
  deletingId,
}: {
  data: PaymentRecord[];
  isLoading: boolean;
  onCreate: () => void;
  onEdit: (payment: PaymentRecord) => void;
  onDelete: (payment: PaymentRecord) => void;
  deletingId?: string;
}) {
  if (isLoading) return <LoadingState />;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <CardTitle className="text-base">Listado de Pagos</CardTitle>
          <Badge className="bg-gray-100 text-gray-700">{data.length} registros</Badge>
        </div>
        <Button size="sm" onClick={onCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Registrar pago
        </Button>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <EmptyState message="No hay pagos registrados en este período" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-3 font-medium">Fecha</th>
                  <th className="pb-3 font-medium">Paciente</th>
                  <th className="pb-3 font-medium">Servicio</th>
                  <th className="pb-3 font-medium">Profesional</th>
                  <th className="pb-3 font-medium">Método</th>
                  <th className="pb-3 font-medium text-right">Monto</th>
                  <th className="pb-3 font-medium text-center">Estado</th>
                  <th className="pb-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.map((p) => (
                  <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-3 whitespace-nowrap">{formatDateTime(p.paidAt)}</td>
                    <td className="py-3">{p.patient?.name || '—'}</td>
                    <td className="py-3">{p.service?.name || '—'}</td>
                    <td className="py-3">{p.professional?.user?.name || '—'}</td>
                    <td className="py-3">
                      <span className="inline-flex items-center gap-1">
                        {(() => {
                          const Icon = methodIcons[p.paymentMethod] || Receipt;
                          return <Icon className="w-3.5 h-3.5 text-gray-500" />;
                        })()}
                        {methodLabels[p.paymentMethod] || p.paymentMethod}
                      </span>
                    </td>
                    <td className="py-3 text-right font-semibold">{formatCurrency(p.amount)}</td>
                    <td className="py-3 text-center">
                      <Badge className={statusColors[p.status] || 'bg-gray-100 text-gray-800'}>
                        {statusLabels[p.status] || p.status}
                      </Badge>
                    </td>
                    <td className="py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => onEdit(p)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onDelete(p)}
                          disabled={deletingId === p.id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PaymentModal({
  title,
  form,
  onChange,
  onClose,
  onSubmit,
  isSubmitting,
  patientOptions,
  professionalOptions,
  serviceOptions,
  boxOptions,
  isEditing,
}: {
  title: string;
  form: PaymentFormState;
  onChange: (field: keyof PaymentFormState, value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  patientOptions: SelectOption[];
  professionalOptions: SelectOption[];
  serviceOptions: SelectOption[];
  boxOptions: SelectOption[];
  isEditing: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500">Gestiona pagos y estados financieros de la clínica.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="grid gap-4 px-6 py-5 md:grid-cols-2">
          {!isEditing && (
            <label className="space-y-2 md:col-span-2">
              <span className="text-sm font-medium text-gray-700">Paciente</span>
              <select
                value={form.patientId}
                onChange={(event) => onChange('patientId', event.target.value)}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Selecciona un paciente</option>
                {patientOptions.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {!isEditing && (
            <label className="space-y-2">
              <span className="text-sm font-medium text-gray-700">Profesional</span>
              <select
                value={form.professionalId}
                onChange={(event) => onChange('professionalId', event.target.value)}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Sin profesional</option>
                {professionalOptions.map((professional) => (
                  <option key={professional.id} value={professional.id}>
                    {professional.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {!isEditing && (
            <label className="space-y-2">
              <span className="text-sm font-medium text-gray-700">Servicio</span>
              <select
                value={form.serviceId}
                onChange={(event) => onChange('serviceId', event.target.value)}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Sin servicio</option>
                {serviceOptions.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {!isEditing && (
            <label className="space-y-2">
              <span className="text-sm font-medium text-gray-700">Box</span>
              <select
                value={form.boxId}
                onChange={(event) => onChange('boxId', event.target.value)}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Sin box</option>
                {boxOptions.map((box) => (
                  <option key={box.id} value={box.id}>
                    {box.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {!isEditing && (
            <Input
              label="Fecha y hora de pago"
              type="datetime-local"
              value={form.paidAt}
              onChange={(event) => onChange('paidAt', event.target.value)}
            />
          )}

          <Input
            label="Monto"
            type="number"
            min="0"
            step="1000"
            value={form.amount}
            onChange={(event) => onChange('amount', event.target.value)}
            placeholder="45000"
          />

          <label className="space-y-2">
            <span className="text-sm font-medium text-gray-700">Método de pago</span>
            <select
              value={form.paymentMethod}
              onChange={(event) => onChange('paymentMethod', event.target.value)}
              className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            >
              {Object.entries(methodLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-2">
            <span className="text-sm font-medium text-gray-700">Estado</span>
            <select
              value={form.status}
              onChange={(event) => onChange('status', event.target.value)}
              className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            >
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-2 md:col-span-2">
            <span className="text-sm font-medium text-gray-700">Descripción</span>
            <textarea
              value={form.description}
              onChange={(event) => onChange('description', event.target.value)}
              rows={4}
              placeholder="Detalle del pago, referencia o comentario interno"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>

        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} loading={isSubmitting}>
            {isEditing ? 'Guardar cambios' : 'Registrar pago'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============ SHARED COMPONENTS ============

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
      <BarChart3 className="w-12 h-12 mb-3 opacity-40" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}
